import { useFormContext } from "react-hook-form";
import { activeStorageBlobUrl } from "@/react/helpers/urlHelpers";
import {
  useAttachmentMetadata,
  useAttachments,
} from "@circle-react-shared/BuilderUI/Media/AttachmentContext";
import type { InlineAttachment } from "./MediaPicker/useMediaPicker";

export interface AttachmentUploaderProps {
  name: string;
}

export interface AttachmentFormData {
  [key: string]:
    | string
    | {
        signed_id: string;
        url: string;
        filename: string;
        byte_size: number;
      };
  text: string;
}

export const useAttachmentUploader = ({ name }: AttachmentUploaderProps) => {
  const { watch, setValue } = useFormContext<AttachmentFormData>();
  const { addLocalAttachment } = useAttachments();

  const signed_id = watch(`${name}.signed_id`, "");

  const attachmentMetadata = useAttachmentMetadata(signed_id);

  const { url = "", filename = "", byte_size = 0 } = attachmentMetadata || {};

  const handleOnUploadStarted = () => {
    setValue(`${name}.signed_id`, "");
  };

  const handleOnUploadComplete = (file: InlineAttachment) => {
    const { signed_id = "", filename = "" } = file;

    const url = activeStorageBlobUrl({
      signed_id,
      filename,
    });

    setValue(`${name}.signed_id`, signed_id);
    setValue("text", filename); // Set the text field to the filename
    addLocalAttachment({
      signed_id,
      url,
      filename,
    });
  };

  const handleOnClickRemove = () => {
    setValue(name, {
      signed_id: "",
      url: "",
      filename: "",
      byte_size: 0,
    });
    setValue("text", "File attachment"); // Clear the text field when attachment is removed
  };

  return {
    signed_id,
    url,
    filename,
    byte_size,
    handleOnUploadStarted,
    handleOnUploadComplete,
    handleOnClickRemove,
  };
};
